import { injectable, inject } from "tsyringe";
import { TOKEN_IProjectSyncing, type IProjectSyncing } from "./ProjectSyncing";
import ProjectWorld, { ProjectEventInputs, ProjectEventType, ProjectLocalEvent, ProjectProjectionResult } from "@/ProjectWorld";
import { type IProjectWorldReader, TOKEN_IProjectWorldReader } from "./ProjectWorldReader";
import { type IUserIdentity, TOKEN_IUserIdentity } from "./UserIdentity";
import { TOKEN_IWrapTime, type IWrapTime } from "./WrapTime";
import { getRandomUsername } from "@/lib/random-username";
import { AccessLevel, ServerEvent } from '@utilitydelta/eventplanedb-client';
import { TOKEN_IWrapAuth0, type IWrapAuth0 } from "./WrapAuth0";
import { EventProcessor } from "@utilitydelta/clientside-event-sourcing";
import { TOKEN_IServerCalls, type IServerCalls } from "./ServerCalls";
import { generateShortClientIdentity } from "@utilitydelta/nosignup-js";
import { ProjectBusEvents, TOKEN_IProjectBus, type IProjectBus } from "./ProjectBus";
import { importRsaPublicKeyForEncryption } from "@utilitydelta/nosignup-js";
import { encryptAsymmetric } from "@utilitydelta/nosignup-js";
import { str2ab } from "@utilitydelta/nosignup-js";
import { arrayBufferToBase64 } from "@utilitydelta/nosignup-js";

export const TOKEN_IProjectEventGenerator = Symbol('IProjectEventGenerator');

export interface IProjectEventGenerator {
    askForSymmetricKey(projectId: string): Promise<void>;
    setSpecificColor(projectId: string, x: number, y: number, color: number): Promise<void>;
    userVisit(projectId: string): unknown;
    joinedUsingShareLink(projectId: string, share_hash: string | undefined): Promise<void>;
    assignRandomUsername(projectId: string): Promise<void>;
    setColor(projectId: string, x: number, y: number, clearOperation: boolean): Promise<void>;
    createColorSquare(projectId: string, gridSize: number): Promise<void>;
    addShareLink(projectId: string, isFork: boolean, accessLevel: AccessLevel, isSingleUse: boolean, description: string | undefined, expiresOn: number): Promise<string | undefined>;
    disableShareLink(projectId: string, shareLinkId: string): Promise<void>;
    userOrClientDisabled(projectId: string, id: string, isOAuthUser: boolean): Promise<void>;
}

@injectable()
export class ProjectEventGenerator implements IProjectEventGenerator {

    constructor(
        @inject(TOKEN_IProjectSyncing) private projectSyncing: IProjectSyncing,
        @inject(TOKEN_IProjectWorldReader) private projectWorldReader: IProjectWorldReader,
        @inject(TOKEN_IUserIdentity) private userIdentity: IUserIdentity,
        @inject(TOKEN_IWrapAuth0) private wrapAuth0: IWrapAuth0,
        @inject(TOKEN_IWrapTime) private wrapTime: IWrapTime,
        @inject(TOKEN_IServerCalls) private serverCalls: IServerCalls,
        @inject(TOKEN_IProjectBus) projectBus: IProjectBus,
    ) {
        projectBus.subscribe_projectionUpdated(this.onProjectionUpdated);
    }

    public userOrClientDisabled = async (projectId: string, id: string, isOAuthUser: boolean): Promise<void> => {
        // First disable on the server
        await this.disableUserOrClient(projectId, id, isOAuthUser);

        // Then create a local event to set access level to none
        const projectWorld = await this.projectWorldReader.mustGet(projectId);

        const processor = projectWorld.eventProcessors.getProcessor(ProjectEventType.ProvideAccess);
        const event = await this.createEventTemplate(projectWorld, processor!);
        const success = processor!.createLocalEvent(event, {
            kind: 'ProvideAccess_EventInputs',
            accessLevel: AccessLevel.None,
            clientId: isOAuthUser ? id : id, // Use id as clientId for non-OAuth users
            userId: isOAuthUser ? id : undefined, // Use id as userId for OAuth users
            shareLinkId: undefined
        });
        if (!success) return;

        await this.projectSyncing.addLocalEvent(projectId, event);
    }

    private disableUserOrClient = async (projectId: string, id: string, isOAuthUser: boolean): Promise<void> => {
        if (isOAuthUser) {
            await this.serverCalls.disableUser(projectId, id);
        } else {
            await this.serverCalls.disableClient(projectId, id);
        }
    }

    public disableShareLink = async (projectId: string, shareLinkId: string): Promise<void> => {
        const projectWorld = await this.projectWorldReader.mustGet(projectId);

        await this.serverCalls.disableShare(projectId, shareLinkId);

        const processor = projectWorld.eventProcessors.getProcessor(ProjectEventType.DisableShareLink);
        const event = await this.createEventTemplate(projectWorld, processor!);
        const success = processor!.createLocalEvent(event, {
            kind: 'DisableShareLink_EventInputs',
            shareLinkId
        });
        if (!success) return;

        await this.projectSyncing.addLocalEvent(projectId, event);
    }

    private onProjectionUpdated = async (data: ProjectBusEvents['PROJECTION_UPDATED']): Promise<void> => {
        if (data.mutationResult.kind !== 'AskedForSymmetricKey_ProjectionResult') return;

        const clientId = (await this.userIdentity.publicKey()).publicKeyHashed;
        const projectWorld = await this.projectWorldReader.mustGet(data.mutationResult.projectId);

        //Only the creating machine should provide the symmetric key
        if (clientId !== projectWorld.projectMetadata.createdByClientId) return;

        //Encrypt the symmetric key using the provided data.mutationResult.publicKey
        const symmetricKey = projectWorld.encryptedProject?.getSymmetricKey();
        if (symmetricKey === undefined) return;

        try {
            // Import the requesting client's public key for encryption
            const requestingPublicKey = await importRsaPublicKeyForEncryption(data.mutationResult.publicKey);

            // Convert symmetric key to ArrayBuffer
            const symmetricKeyBuffer = str2ab(symmetricKey);

            // Encrypt the symmetric key using the requesting client's public key
            const encryptedKeyBuffer = await encryptAsymmetric(requestingPublicKey, symmetricKeyBuffer);

            // Convert to base64 for transmission
            const encryptedSymmetricKey = arrayBufferToBase64(encryptedKeyBuffer);

            //Raise the event
            const processor = projectWorld.eventProcessors.getProcessor(ProjectEventType.ProvidedSymmetricKey);
            const event = await this.createEventTemplate(projectWorld, processor!);
            const success = processor!.createLocalEvent(event, {
                kind: 'ProvidedSymmetricKey_EventInputs',
                encryptedSymmetricKey,
                publicKey: data.mutationResult.publicKey
            });
            if (!success) return;

            await this.projectSyncing.addLocalEvent(data.mutationResult.projectId, event);
        } catch (error) {
            console.error('Failed to encrypt symmetric key:', error);
            return;
        }
    }

    askForSymmetricKey = async (projectId: string): Promise<void> => {
        const publicKey = (await this.userIdentity.publicKey()).publicKeyStr;

        const projectWorld = await this.projectWorldReader.mustGet(projectId);
        const processor = projectWorld.eventProcessors.getProcessor(ProjectEventType.AskedForSymmetricKey);
        const event = await this.createEventTemplate(projectWorld, processor!);
        const success = processor!.createLocalEvent(event, {
            kind: 'AskedForSymmetricKey_EventInputs',
            publicKey
        });
        if (!success) return;

        await this.projectSyncing.addLocalEvent(projectId, event);
    }

    private async createEventTemplate(
        projectWorld: ProjectWorld,
        processor: EventProcessor<ProjectEventType, ProjectEventInputs, ProjectLocalEvent, ProjectProjectionResult, ProjectLocalEvent, ServerEvent<ProjectEventType>>): Promise<ProjectLocalEvent> {

        const eventDate = this.wrapTime.getCurrentUnixTimestamp();
        const publicKey = (await this.userIdentity.publicKey()).publicKeyHashed;
        const userId = this.wrapAuth0.userIdHashed;

        return projectWorld.createLocalEvent(processor!, eventDate, publicKey, userId);
    }

    setSpecificColor = async (projectId: string, x: number, y: number, color: number): Promise<void> => {
        const projectWorld = await this.projectWorldReader.mustGet(projectId);
        const coloredsquareId = `${x}_${y}`;
        const entity = projectWorld.coloredSquares.maybeGet(coloredsquareId);
        if (entity !== undefined && entity.color === color) return; // Color already set to the same value

        const processor = projectWorld.eventProcessors.getProcessor(ProjectEventType.SetColor);
        const event = await this.createEventTemplate(projectWorld, processor!);
        const success = processor!.createLocalEvent(event, {
            kind: 'SetColor_EventInputs',
            x: x,
            y: y,
            color,
        });
        if (!success) return;

        await this.projectSyncing.addLocalEvent(projectId, event);
    }

    public addShareLink = async (projectId: string, isFork: boolean, accessLevel: AccessLevel, isSingleUse: boolean, description: string | undefined, expiresOn: number): Promise<string | undefined> => {

        await this.projectSyncing.intentionalOptInForServer(projectId);

        const projectWorld = await this.projectWorldReader.mustGet(projectId);

        // Generate unique share link ID on the server
        let share_key: string | undefined = undefined;
        try {
            share_key = (await this.serverCalls.share(projectId, accessLevel, isSingleUse, expiresOn)).share_key;
        } catch {
            return share_key
        }

        const shareLinkId = await generateShortClientIdentity(share_key);

        const processor = projectWorld.eventProcessors.getProcessor(ProjectEventType.AddShareLink);
        const event = await this.createEventTemplate(projectWorld, processor!);
        const success = processor!.createLocalEvent(event, {
            kind: 'AddShareLink_EventInputs',
            shareLinkId,
            accessLevel,
            expiresOn,
            description,
            isSingleUse,
        });
        if (!success) return undefined;

        await this.projectSyncing.addLocalEvent(projectId, event);

        const full = location.protocol + '//' + location.host;
        const forkModifyer = isFork ? "&fork=true" : "";
        const sk = projectWorld.projectMetadata.symmetricKey !== undefined ? `&sk=${projectWorld.projectMetadata.symmetricKey}` : "";
        return `${full}/project/${projectId}#sharekey=${share_key}${forkModifyer}${sk}`;
    }

    public userVisit = async (projectId: string): Promise<void> => {
        const visitTime = this.wrapTime.getCurrentUnixTimestamp();
        const userId = this.wrapAuth0.userIdHashed;
        const clientId = (await this.userIdentity.publicKey()).publicKeyHashed;
        const id = userId ?? clientId;

        const projectWorld = await this.projectWorldReader.mustGet(projectId);
        const projectUser = projectWorld.projectUsers.maybeGetIgnoreDeleted(id);
        if (!projectUser || projectUser && projectUser.accessLevel === AccessLevel.Viewer) return; //No Access to log a user visit

        const processor = projectWorld.eventProcessors.getProcessor(ProjectEventType.UserVisit);
        const event = await this.createEventTemplate(projectWorld, processor!);
        const success = processor!.createLocalEvent(event, {
            kind: 'UserVisit_EventInputs',
            visitTime,
            userId,
            clientId
        });
        if (!success) return;

        await this.projectSyncing.addLocalEvent(projectId, event);
    }

    public joinedUsingShareLink = async (projectId: string, shareLinkId: string | undefined): Promise<void> => {

        const projectWorld = await this.projectWorldReader.mustGet(projectId);

        const userId = this.wrapAuth0.userIdHashed;
        const clientId = (await this.userIdentity.publicKey()).publicKeyHashed;

        const shareEntity = shareLinkId !== undefined ? projectWorld.shareLinks.maybeGetIgnoreDeleted(shareLinkId) : undefined;

        let accessLevel: AccessLevel = AccessLevel.None;
        if (shareEntity !== undefined) {
            accessLevel = shareEntity.accessLevel;
        }

        if (accessLevel === AccessLevel.Viewer || accessLevel === AccessLevel.None) return; // Unable to write events

        // Add a provide access event, then generate a random username
        const processor = projectWorld.eventProcessors.getProcessor(ProjectEventType.ProvideAccess);
        const event = await this.createEventTemplate(projectWorld, processor!);
        const success = processor!.createLocalEvent(event, {
            kind: 'ProvideAccess_EventInputs',
            accessLevel,
            clientId,
            shareLinkId,
            userId
        });
        if (!success) return;

        await this.projectSyncing.addLocalEvent(projectId, event);
    }

    public assignRandomUsername = async (projectId: string): Promise<void> => {
        const projectWorld = await this.projectWorldReader.mustGet(projectId);

        const userId = this.wrapAuth0.userIdHashed;
        const clientId = (await this.userIdentity.publicKey()).publicKeyHashed;
        const id = userId ?? clientId;
        const projectUser = projectWorld.projectUsers.maybeGetIgnoreDeleted(id);
        if (projectUser && projectUser.randomUsername !== undefined) return; // Username already assigned
        if (projectUser && projectUser.accessLevel === AccessLevel.Viewer) return; //No Access to assign username

        const processor = projectWorld.eventProcessors.getProcessor(ProjectEventType.AssignRandomUsername);
        const event = await this.createEventTemplate(projectWorld, processor!);
        const success = processor!.createLocalEvent(event, {
            kind: 'AssignRandomUsername_EventInputs',
            randomUsername: getRandomUsername(),
            userId,
            clientId
        });
        if (!success) return;

        await this.projectSyncing.addLocalEvent(projectId, event);
    }

    setColor = async (projectId: string, x: number, y: number, clearOperation: boolean) => {
        const projectWorld = await this.projectWorldReader.mustGet(projectId);
        const processor = projectWorld.eventProcessors.getProcessor(ProjectEventType.SetColor);

        const event = await this.createEventTemplate(projectWorld, processor!);
        const success = processor!.createLocalEvent(event, {
            kind: 'SetColor_EventInputs',
            x: x,
            y: y,
            color: clearOperation ? 0 : projectWorld.projectMetadata.myColor,
        });
        if (!success) return;

        await this.projectSyncing.addLocalEvent(projectId, event);
    }

    createColorSquare = async (projectId: string, gridSize: number) => {
        const projectWorld = await this.projectWorldReader.mustGet(projectId);

        const processor = projectWorld.eventProcessors.getProcessor(ProjectEventType.ColorSquareCreated);

        const event = await this.createEventTemplate(projectWorld, processor!);
        const success = processor!.createLocalEvent(event, {
            kind: 'ColorSquareCreated_EventInputs',
            gridSize: gridSize,
        });

        if (!success) return;

        await this.projectSyncing.addLocalEvent(projectId, event);
    }
}
